import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Group,
    Modal,
    Paper,
    SegmentedControl,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useWindowEvent} from '@mantine/hooks'
import {notifications} from '@mantine/notifications'
import {IconTrash} from '@tabler/icons-react'
import {addDays} from 'date-fns'
import {useMemo, useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useCurrentUser} from 'src/auth/use-current-user'
import {driversForYard, noHomeYardSummary} from 'src/dispatch/availability/drivers-by-yard'
import {
    type AbsenceReason,
    absenceReasonOptions,
    type DriverDay,
    type WorkerAbsence,
} from 'src/dispatch/availability/schemas'
import {
    useAddAbsence,
    useDriversForDay,
    useRemoveAbsence,
    useWorkerAbsences,
} from 'src/dispatch/availability/use-availability'
import {canChangeYardItem} from 'src/jobs/edit-rights'
import {formatJobDate, toApiDateString} from 'src/jobs/job-schedule-format'
import {alertToneColor} from 'src/theme/semantic-colors'
import {ConfirmDialog} from 'src/ui/confirm-dialog'

const GROUPS = [
    {key: 'available', title: 'Available', color: 'green'},
    {key: 'on_jobs', title: 'On jobs', color: 'blue'},
    {key: 'off', title: 'Sick / PTO / off', color: 'grape'},
    {key: 'out_of_hours', title: 'Out of hours', color: 'orange'},
] as const
type GroupKey = (typeof GROUPS)[number]['key']

const CLASS_TAG: Record<NonNullable<DriverDay['employmentClassification']>, string> = {
    w2_employee: 'W-2',
    '1099_contractor': 'O-O',
}

function formatAbsenceDayRange(absence: Pick<WorkerAbsence, 'startDate' | 'endDate'>): string {
    const first = formatJobDate(absence.startDate)
    return absence.endDate !== absence.startDate
        ? `${first} to ${formatJobDate(absence.endDate)}`
        : first
}

function driverGroupKeyForDay(driver: DriverDay): GroupKey {
    if (driver.state === 'available' || driver.state === 'on_jobs') return driver.state
    if (driver.state === 'out_of_hours') return 'out_of_hours'

    return 'off'
}

type DriversPanelProps = {
    yardId: string | null
    yardName: string | null
    onChanged?: () => Promise<void> | void
}

export function DriversPanel({yardId, yardName, onChanged}: DriversPanelProps) {
    // Re-render on window focus to recompute the business date.
    const [, setFocusCount] = useState(0)
    useWindowEvent('focus', () => setFocusCount((count) => count + 1))
    const today = businessCalendarDate()
    const [dayChoice, setDayChoice] = useState<'today' | 'tomorrow'>('today')
    const [open, setOpen] = useState(false)
    const [editing, setEditing] = useState<DriverDay | null>(null)
    const day = toApiDateString(dayChoice === 'today' ? today : addDays(today, 1))
    const {data, isLoading} = useDriversForDay(day)

    const grouped = useMemo(() => {
        const result: Record<GroupKey, DriverDay[]> = {
            available: [],
            on_jobs: [],
            off: [],
            out_of_hours: [],
        }
        for (const driver of driversForYard(data?.drivers ?? [], yardId)) {
            result[driverGroupKeyForDay(driver)].push(driver)
        }
        return result
    }, [data, yardId])
    const noHomeYard = noHomeYardSummary(data?.drivers ?? [])

    return (
        <Paper withBorder radius="md" p="sm" mb="sm" data-testid="drivers-panel">
            <Group justify="space-between" wrap="wrap" gap="xs">
                <Group gap="xs" wrap="wrap">
                    <Text fw={700}>Drivers{yardName ? ` · ${yardName}` : ''}</Text>
                    <SegmentedControl
                        size="xs"
                        aria-label="Drivers for today or tomorrow"
                        value={dayChoice}
                        onChange={(value) => setDayChoice(value as 'today' | 'tomorrow')}
                        data={[
                            {value: 'today', label: 'Today'},
                            {value: 'tomorrow', label: 'Tomorrow'},
                        ]}
                    />
                    {GROUPS.map((group) => (
                        <Badge key={group.key} color={group.color} variant="light">
                            {grouped[group.key].length} {group.title.toLowerCase()}
                        </Badge>
                    ))}
                    {noHomeYard ? (
                        <Text size="xs" c="dimmed">
                            {yardId == null ? `Includes ${noHomeYard}` : `Excludes ${noHomeYard}`}
                        </Text>
                    ) : null}
                </Group>
                <Button
                    size="compact-sm"
                    variant="subtle"
                    aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}
                >
                    {open ? 'Hide drivers' : 'Show drivers'}
                </Button>
            </Group>
            {open ? (
                <Stack gap="xs" mt="sm">
                    {data?.hoursNote ? (
                        <Text size="xs" c="dimmed">
                            {data.hoursNote} Nobody is shown as out of hours.
                        </Text>
                    ) : null}
                    {isLoading ? <Text size="sm">Loading drivers…</Text> : null}
                    <SimpleGrid cols={{base: 1, sm: 2, lg: 4}} spacing="sm">
                        {GROUPS.map((group) => (
                            <Stack key={group.key} gap={4}>
                                <Text size="sm" fw={600} c={`${group.color}.8`}>
                                    {group.title} ({grouped[group.key].length})
                                </Text>
                                {grouped[group.key].map((driver) => (
                                    <Group
                                        key={driver.workerId}
                                        gap={6}
                                        wrap="nowrap"
                                        justify="space-between"
                                    >
                                        <Stack gap={0} style={{minWidth: 0}}>
                                            <Text size="sm" truncate>
                                                {driver.name}{' '}
                                                {driver.employmentClassification ? (
                                                    <Text span size="xs" c="dimmed">
                                                        {CLASS_TAG[driver.employmentClassification]}
                                                    </Text>
                                                ) : null}
                                                {yardId == null &&
                                                driver.homeDispatchYardId == null ? (
                                                    <Text span size="xs" c="dimmed">
                                                        {' · '}No home yard
                                                    </Text>
                                                ) : null}
                                            </Text>
                                            {driver.detail ? (
                                                <Text size="xs" c="dimmed" truncate>
                                                    {group.key === 'off'
                                                        ? `${driver.label} · `
                                                        : ''}
                                                    {driver.detail}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                        <Button
                                            size="compact-xs"
                                            variant="subtle"
                                            aria-label={`Time off for ${driver.name}`}
                                            onClick={() => setEditing(driver)}
                                        >
                                            Time off
                                        </Button>
                                    </Group>
                                ))}
                            </Stack>
                        ))}
                    </SimpleGrid>
                </Stack>
            ) : null}
            <TimeOffModal
                key={`${editing?.workerId ?? 'none'}-${day}`}
                driver={editing}
                defaultDay={day}
                onClose={() => setEditing(null)}
                onChanged={onChanged}
            />
        </Paper>
    )
}

type TimeOffFormValues = {
    reason: AbsenceReason
    startDate: string
    endDate: string
    note: string
}

function TimeOffModal({
    driver,
    defaultDay,
    onClose,
    onChanged,
}: {
    driver: DriverDay | null
    defaultDay: string
    onClose: () => void
    onChanged?: () => Promise<void> | void
}) {
    const currentUser = useCurrentUser()
    const canChange =
        currentUser == null || canChangeYardItem(currentUser, driver?.homeDispatchYardId)
    const {data: absences = []} = useWorkerAbsences(driver?.workerId ?? null)
    const addAbsence = useAddAbsence()
    const removeAbsence = useRemoveAbsence()
    const [confirmingRemove, setConfirmingRemove] = useState<WorkerAbsence | null>(null)
    const removingId = removeAbsence.isPending ? removeAbsence.variables : null
    const form = useForm<TimeOffFormValues>({
        initialValues: {reason: 'sick', startDate: defaultDay, endDate: defaultDay, note: ''},
        validateInputOnChange: ['endDate'],
        validate: {
            endDate: (value, values) =>
                value && values.startDate && value < values.startDate
                    ? 'Last day off cannot be before first day off'
                    : null,
        },
    })

    async function remove(absenceId: string) {
        try {
            await removeAbsence.mutateAsync(absenceId)
            await onChanged?.()
        } catch (error) {
            notifications.show({
                title: 'Could not remove time off',
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
        }
    }

    async function save(values: TimeOffFormValues) {
        if (!driver) return
        try {
            await addAbsence.mutateAsync({workerId: driver.workerId, input: values})
            await onChanged?.()
            notifications.show({
                title: 'Time off saved',
                message: `${driver.name} cannot be assigned those days`,
                color: alertToneColor('success'),
            })
            form.setFieldValue('note', '')
        } catch (error) {
            notifications.show({
                title: 'Could not save time off',
                message: error instanceof Error ? error.message : 'Try again.',
                color: alertToneColor('danger'),
            })
        }
    }

    const startDateProps = form.getInputProps('startDate')

    return (
        <Modal
            opened={driver != null}
            onClose={onClose}
            title={driver ? `Time off · ${driver.name}` : ''}
            size="md"
        >
            <form onSubmit={form.onSubmit((values) => void save(values))}>
                <Stack gap="sm">
                    {absences.length > 0 ? (
                        <Stack gap={4}>
                            <Text size="sm" fw={600}>
                                Current and upcoming
                            </Text>
                            {absences.map((absence) => (
                                <Group key={absence.id} justify="space-between" wrap="nowrap">
                                    <Text size="sm">
                                        {absence.label}: {formatAbsenceDayRange(absence)}
                                        {absence.note ? ` · ${absence.note}` : ''}
                                    </Text>
                                    {canChange ? (
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            aria-label={`Remove time off: ${absence.label} ${formatAbsenceDayRange(absence)}`}
                                            loading={removingId === absence.id}
                                            disabled={
                                                removingId != null && removingId !== absence.id
                                            }
                                            onClick={() => setConfirmingRemove(absence)}
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    ) : null}
                                </Group>
                            ))}
                        </Stack>
                    ) : (
                        <Alert variant="light" color="gray">
                            No time off recorded
                        </Alert>
                    )}
                    {canChange ? (
                        <>
                            <Text size="sm" fw={600}>
                                Add time off
                            </Text>
                            <Select
                                label="Reason"
                                data={absenceReasonOptions}
                                allowDeselect={false}
                                {...form.getInputProps('reason')}
                            />
                            <Group grow align="flex-start">
                                <TextInput
                                    type="date"
                                    label="First day off"
                                    {...startDateProps}
                                    onChange={(event) => {
                                        const value = event.currentTarget.value
                                        form.setFieldValue('startDate', value)
                                        if (form.getValues().endDate < value) {
                                            form.setFieldValue('endDate', value)
                                        }
                                    }}
                                />
                                <TextInput
                                    type="date"
                                    label="Last day off"
                                    min={form.values.startDate}
                                    {...form.getInputProps('endDate')}
                                />
                            </Group>
                            <TextInput label="Note (optional)" {...form.getInputProps('note')} />
                        </>
                    ) : (
                        <Text size="sm" c="dimmed">
                            Time off is view only. Ask{' '}
                            {driver?.homeDispatchYardId ? 'their yard' : 'dispatch'} to change it.
                        </Text>
                    )}
                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={onClose}>
                            Close
                        </Button>
                        {canChange ? (
                            <Button
                                type="submit"
                                loading={addAbsence.isPending}
                                disabled={
                                    !form.values.startDate ||
                                    !form.values.endDate ||
                                    !form.isValid()
                                }
                            >
                                Save time off
                            </Button>
                        ) : null}
                    </Group>
                </Stack>
            </form>
            <ConfirmDialog
                opened={confirmingRemove != null}
                title="Remove time off?"
                message={
                    confirmingRemove
                        ? `${driver?.name ?? 'The driver'} will show as free on ${formatAbsenceDayRange(confirmingRemove)}.`
                        : ''
                }
                confirmLabel="Remove time off"
                cancelLabel="Keep it"
                onCancel={() => setConfirmingRemove(null)}
                onConfirm={() => {
                    const absence = confirmingRemove
                    setConfirmingRemove(null)
                    if (absence) void remove(absence.id)
                }}
            />
        </Modal>
    )
}
